import isFunction from 'lodash/isFunction'
import type { FunctionComponent, ReactNode } from 'react'
import React from 'react'
import type { FormikProps } from 'formik'
import type { ModalProps, ModalTriggerProps } from '@ztechapps/webui.modal'
import { ModalDragHandle, Modal as ModalCore } from '@ztechapps/webui.modal'
import type { SaveCancelButtonsProps } from '@ztechapps/webui.button'
import { BusyButton, SaveCancelButtons } from '@ztechapps/webui.button'
import { Modal } from 'react-bootstrap'
import type { FormProps } from '../Form'
import { Form } from '../Form'
import { ShowIf } from '@ztechapps/webui.util'

type FormModalProps<TValue = any> = {
  trigger?: FunctionComponent<ModalTriggerProps>
  triggerIcon?: string
  title?: ReactNode
  saveText?: string
  children?: ReactNode
  show?: boolean
  onApply?: any
  saveCancelButtonsProps?: SaveCancelButtonsProps
  // Optional override for the whole footer row, so a caller whose submit control lives
  // elsewhere (a toolbar-mounted send button, for instance) is not stuck with the built-in
  // save/cancel pair. Omitted → the built-in Apply / Save-Cancel row renders. A caller that
  // supplies one owns its full contents, including any Cancel control the modal still needs
  // to stay dismissable.
  footer?: FunctionComponent<FormikProps<TValue>>
} & Omit<ModalProps, 'children'> &
  Pick<FormProps<TValue>, 'initialValues' | 'onSubmit' | 'onReset' | 'validate' | 'validateOnBlur'>
/**
 * This is a component with formik inside but encapsulate it self with modal form
 * it also has the delete, save, cancel button
 * this is the replacement of withFormModal
 */
export function FormModal<TValue = any>({
  size,
  dialogClassName,
  trigger,
  triggerIcon = 'plus',
  title,
  saveText,
  children,
  initialValues,
  onSubmit,
  onReset,
  validate,
  validateOnBlur,
  show,
  onHide,
  onApply,
  saveCancelButtonsProps,
  footer: Footer,
  resizable,
  resizeStorageKey,
  container,
}: FormModalProps<TValue>) {
  saveText = saveCancelButtonsProps?.saveText || saveText || 'Add'

  // Called as a plain function, never rendered as a component declared inside this render:
  // a component declared here is a new element type on every render, so React remounts the
  // footer — a Save click that straddles a re-render lands on a detached button and submits nothing.
  // Same caller-component-or-default-render-function pattern as
  // libs/webui/data-tools/src/DataTable/table-header/TableHeader.tsx:13,29.
  const renderDefaultFooter = ({ values, submitCount, isValid, isSubmitting }: FormikProps<TValue>) => (
    <div className={'d-flex justify-content-between flex-fill'}>
      <div>
        <ShowIf condition={isFunction(onApply)}>
          <BusyButton
            bsStyle={'warning'}
            text={'Apply'}
            onClick={() => {
              onApply?.(values)
            }}
          />
        </ShowIf>
      </div>

      <SaveCancelButtons
        {...(saveCancelButtonsProps ?? {})}
        saveText={saveText}
        saveType="submit"
        cancelType="reset"
        disabled={submitCount > 0 && !isValid}
        isBusy={isSubmitting}
      />
    </div>
  )

  return (
    <ModalCore
      {...{
        triggerIcon,
        dialogClassName,
        trigger,
        size,
        show,
        onHide,
        resizable,
        resizeStorageKey,
        container,
      }}
    >
      {({ toggleShowModal }) => (
        <Form<TValue>
          {...{
            initialValues,
            onSubmit,
            onReset,
            validate,
            validateOnBlur,
            afterSubmit: toggleShowModal,
            afterReset: toggleShowModal,
          }}
        >
          {(formikProps) => {
            const { isSubmitting, ...rest } = formikProps
            return (
              <React.Fragment>
                <ModalDragHandle>{title}</ModalDragHandle>
                <Modal.Body>{isFunction(children) ? children({ isSubmitting, ...rest }) : children}</Modal.Body>
                <Modal.Footer>{Footer ? <Footer {...formikProps} /> : renderDefaultFooter(formikProps)}</Modal.Footer>
              </React.Fragment>
            )
          }}
        </Form>
      )}
    </ModalCore>
  )
}
